import { isAxiosError } from 'axios';
import {
  PRODUCT_CATEGORY_LINK_BULK_DEFAULT_ERROR_MESSAGE,
  PRODUCT_CATEGORY_LINK_BULK_ERROR_MESSAGE,
  PRODUCT_CATEGORY_LINK_BULK_JOB_STORAGE_KEY_PREFIX,
  PRODUCT_CATEGORY_LINK_BULK_POLL_FALLBACK_INTERVAL_MS,
  PRODUCT_CATEGORY_LINK_BULK_POLL_STEPS,
  PRODUCT_CATEGORY_LINK_CSV_HEADER,
  PRODUCT_CATEGORY_LINK_CSV_SAMPLE_ROWS,
} from '../../constants/service/app/productCategoryLinkBulkConstants';
import {
  BULK_UPLOAD_ES_SYNC_STATUS,
  BULK_UPLOAD_JOB_EXPIRED,
  BULK_UPLOAD_JOB_STATUS,
  BulkUploadEsSyncSummary,
  BulkUploadJobQueryData,
  BulkUploadJobRecord,
} from '../../types/category/bulkUpload';

/**
 * 메인 잡이 종결됐는가 — 완료·실패·만료.
 * delayed / paused / stuck 은 잡이 살아 있는 상태라 종결이 아니다.
 * 업로드 버튼 잠금(isBusy)·localStorage 정리·리스트 갱신이 이 판정을 기준으로 한다.
 */
export const isBulkJobSettled = (data: BulkUploadJobQueryData | undefined): boolean => {
  if (!data) {
    return false;
  }

  return (
    data.status === BULK_UPLOAD_JOB_STATUS.COMPLETED ||
    data.status === BULK_UPLOAD_JOB_STATUS.FAILED ||
    data.status === BULK_UPLOAD_JOB_EXPIRED
  );
};

/** ES 동기화가 더 진행될 여지가 없는가 (집계가 없으면 추적 불가 — 종결 취급) */
export const isBulkEsSyncSettled = (esSync: BulkUploadEsSyncSummary | undefined): boolean => {
  if (!esSync) {
    return true;
  }

  return (
    esSync.status === BULK_UPLOAD_ES_SYNC_STATUS.NOT_REQUESTED ||
    esSync.status === BULK_UPLOAD_ES_SYNC_STATUS.COMPLETED ||
    esSync.status === BULK_UPLOAD_ES_SYNC_STATUS.FAILED
  );
};

/**
 * 폴링을 완전히 멈춰도 되는가.
 *
 * 메인 잡 종결과 폴링 종결을 분리한 이유: 메인 잡이 completed 여도 ES 검색 인덱스
 * 동기화는 백그라운드에서 계속되고, 완료 카드가 그 진행 수치를 보여준다. 여기서
 * 폴링을 끊으면 수치가 스냅샷으로 얼어붙는다. 반대로 업로드 버튼은 메인 잡 종결
 * 시점에 풀려야 한다 — 백엔드 중복 차단 마커가 그 시점에 해제되기 때문이다.
 */
export const isBulkJobPollingDone = (data: BulkUploadJobQueryData | undefined): boolean => {
  if (!data || !isBulkJobSettled(data)) {
    return false;
  }

  if (data.status !== BULK_UPLOAD_JOB_STATUS.COMPLETED) {
    // 실패·만료 — 더 받을 것이 없다
    return true;
  }

  return isBulkEsSyncSettled(data.esSync);
};

/**
 * 경과 시간에 맞는 폴링 간격(ms).
 * 구간 테이블의 untilMs 미만이면 그 구간의 간격을, 전부 넘어섰으면 fallback 을 쓴다.
 */
export const resolveBulkJobPollInterval = (elapsedMs: number): number => {
  const step = PRODUCT_CATEGORY_LINK_BULK_POLL_STEPS.find(({ untilMs }) => elapsedMs < untilMs);

  return step ? step.intervalMs : PRODUCT_CATEGORY_LINK_BULK_POLL_FALLBACK_INTERVAL_MS;
};

/**
 * 에러 코드를 한글 메시지로 바꾼다 — 잡 failedReason 과 클라이언트 사전 차단 전용.
 *
 * 워커는 failedReason 에 원시 코드만 던진다(throw new Error(ERROR.EXCELUPLOAD007.code)).
 * 이 경로에는 서버 한글이 없으므로 클라이언트 매핑이 유일한 번역 지점이다.
 * S3 장애 등 예기치 못한 에러는 자유 문장이 그대로 오므로, 매핑에 없으면 원문을 노출한다.
 * HTTP 4xx 응답은 resolveBulkUploadHttpErrorMessage 를 쓸 것 — 서버 필터가 한글을 완비한다.
 */
export const resolveBulkUploadErrorMessage = (codeOrMessage: string | null | undefined): string => {
  const trimmed = codeOrMessage?.trim();

  if (!trimmed) {
    return PRODUCT_CATEGORY_LINK_BULK_DEFAULT_ERROR_MESSAGE;
  }

  return PRODUCT_CATEGORY_LINK_BULK_ERROR_MESSAGE[trimmed] ?? trimmed;
};

const CONFLICT_STATUS = 409;

type BulkUploadErrorResponseBody = {
  message?: unknown;
  errors?: { code?: unknown; message?: unknown }[];
};

/**
 * HTTP 4xx 응답에서 사용자에게 보여줄 메시지를 뽑는다.
 *
 * 백엔드 구현(#4914) 확인 결과, admin 의 전역 예외 필터가 모든 4xx 를
 * `{ status, errors: [{ code, message }] }` envelope 으로 감싸고 message 에 한글을 완비한다
 * (libs/shared/filter — msg.ts 에 EXCELUPLOAD001~011 전부 정의됨). 그래서 서버 메시지를
 * 1순위로 쓴다 — 클라이언트 매핑을 우선하면 백엔드 문구와 계속 어긋나게 된다(drift).
 *
 * 주의할 변칙 두 가지:
 * - 409 는 필터(preProcessByClientError)가 code 자리에 기본값 '400' 을 넣는다.
 *   code 를 먼저 읽으면 토스트에 "400" 이 노출되므로 반드시 message 우선.
 * - 미등록 코드는 필터가 message 자리에 코드 원문을 되돌려준다. 그래서 서버 메시지도
 *   매핑에 한 번 통과시킨다(코드면 번역되고, 한글 문장이면 그대로 통과).
 */
export const resolveBulkUploadHttpErrorMessage = (error: unknown): string => {
  if (!isAxiosError(error)) {
    return PRODUCT_CATEGORY_LINK_BULK_DEFAULT_ERROR_MESSAGE;
  }

  const data = error.response?.data as BulkUploadErrorResponseBody | undefined;
  const first = data?.errors?.[0];

  const serverMessage = typeof first?.message === 'string' && first.message ? first.message : null;
  if (serverMessage) {
    return resolveBulkUploadErrorMessage(serverMessage);
  }

  const code = typeof first?.code === 'string' && first.code ? first.code : null;
  if (code) {
    return resolveBulkUploadErrorMessage(code);
  }

  // 평면 형태({ statusCode, message }) 방어 — envelope 을 안 타는 예외 케이스용
  return resolveBulkUploadErrorMessage(typeof data?.message === 'string' ? data.message : null);
};

/**
 * 409 Conflict 라면 진행 중 jobId 를 돌려준다.
 *
 * 중복 차단 마커는 어드민 단위라 진행 중 잡도 본인 것이다. 이 jobId 로 진행 카드를
 * 복원하면 다른 PC 에서 시작한 업로드나 저장소를 비운 경우에도 진행 상황을 볼 수 있다.
 *
 * 현재 백엔드(#4914)는 409 에 jobId 를 싣지 않아 항상 null 이 나온다(스펙 §5.1 과 달리
 * 구현에서 누락 — 백엔드에 요청해 둔 상태). 계약이 채워지는 즉시 복원 경로가 살아나도록
 * 추출 로직은 유지한다.
 */
export const extractDuplicatedJobId = (error: unknown): string | null => {
  if (!isAxiosError(error) || error.response?.status !== CONFLICT_STATUS) {
    return null;
  }

  const jobId = (error.response?.data as { jobId?: unknown } | undefined)?.jobId;

  if (typeof jobId === 'string' && jobId) {
    return jobId;
  }

  // 숫자로 내려오는 경우도 방어 (스펙 예시는 문자열)
  return typeof jobId === 'number' ? String(jobId) : null;
};

/** 샘플 CSV 본문 — BOM 은 Blob 을 만드는 쪽에서 붙인다 */
export const buildProductCategoryLinkCsvSample = (): string =>
  [PRODUCT_CATEGORY_LINK_CSV_HEADER, ...PRODUCT_CATEGORY_LINK_CSV_SAMPLE_ROWS].join('\n');

const buildJobStorageKey = (adminId: number): string =>
  `${PRODUCT_CATEGORY_LINK_BULK_JOB_STORAGE_KEY_PREFIX}:${adminId}`;

/**
 * 진행 중 잡 정보를 읽는다.
 * 값이 손상됐거나 jobId 가 없으면 키를 지우고 null 을 반환한다 — 복원할 수 없는 값을
 * 계속 들고 있으면 매번 404 를 받으며 안내 토스트만 반복하게 된다.
 */
export const readBulkJobRecord = (adminId: number): BulkUploadJobRecord | null => {
  const key = buildJobStorageKey(adminId);

  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);
    const record = parsed as Partial<BulkUploadJobRecord> | null;

    if (!record || typeof record.jobId !== 'string' || !record.jobId) {
      localStorage.removeItem(key);
      return null;
    }

    return {
      jobId: record.jobId,
      fileName: typeof record.fileName === 'string' ? record.fileName : '',
      // startedAt 이 없거나 이상한 값이면 지금부터 센다 (폴링 간격 산출용이라 근사치로 충분)
      startedAt:
        typeof record.startedAt === 'number' && Number.isFinite(record.startedAt) ? record.startedAt : Date.now(),
    };
  } catch {
    try {
      localStorage.removeItem(key);
    } catch {
      // localStorage 접근 자체가 차단된 환경(사파리 프라이빗 등) — 복원만 포기한다
    }
    return null;
  }
};

export const writeBulkJobRecord = (adminId: number, record: BulkUploadJobRecord): void => {
  try {
    localStorage.setItem(buildJobStorageKey(adminId), JSON.stringify(record));
  } catch {
    // 저장 실패(용량 초과·프라이빗 모드 등)해도 업로드 자체는 계속되어야 한다.
    // 복원만 못 하게 되고, 재업로드 시 409 + jobId 로 복구된다.
  }
};

export const clearBulkJobRecord = (adminId: number): void => {
  try {
    localStorage.removeItem(buildJobStorageKey(adminId));
  } catch {
    // 무시 — 다음 읽기에서 손상 값으로 걸러진다
  }
};
